import { ChangeDetectionStrategy, Component, HostListener, computed, inject, input, output, signal, ElementRef } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import {
  ESegmentedToggleComponent,
  ESegmentedToggleOption,
} from '@shared/components/atoms/inputs/e-segmented-toggle/e-segmented-toggle.component';
import { PlatformHdCenterState } from '@data/_generated-api/platform-api/models/platform-hd-center-state';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';

/** Backend-kolom ↔ NL-vertaalkey van `humanDesign.properties.centerNames` (top-down bodygraph-volgorde). */
export const HD_CENTER_FILTER_ROWS: readonly { property: string; nameKey: string }[] = [
  { property: 'HdCenterHead', nameKey: 'hoofd' },
  { property: 'HdCenterAjna', nameKey: 'ajna' },
  { property: 'HdCenterThroat', nameKey: 'keel' },
  { property: 'HdCenterG', nameKey: 'zelf' },
  { property: 'HdCenterHeart', nameKey: 'hart' },
  { property: 'HdCenterSacral', nameKey: 'sacraal' },
  { property: 'HdCenterSpleen', nameKey: 'milt' },
  { property: 'HdCenterSolarPlexus', nameKey: 'solarplexus' },
  { property: 'HdCenterRoot', nameKey: 'root' },
];

/**
 * Filterknop + uitklappaneel voor de negen center-states (Defined/Undefined/Open
 * per center; meerdere centers combineren als AND). Apply-on-close, zoals de
 * eigenschappen-filters: pas bij het sluiten van het paneel wordt geëmit.
 */
/** Waarde van de "–"-knop (geen filter op dit center) in de segmented toggle. */
const ANY_STATE = 'any';

@Component({
  selector: 'hd-chart-center-filter',
  standalone: true,
  imports: [SharedModule, ESegmentedToggleComponent],
  templateUrl: './hd-chart-center-filter.component.html',
  styleUrl: './hd-chart-center-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdChartCenterFilterComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly host = inject(ElementRef<HTMLElement>);

  /** Actieve filters: backend-kolom → gewenste state (alleen echte states, geen Unknown). */
  readonly selected = input<Record<string, number>>({});
  readonly selectedChange = output<Record<string, number>>();

  protected readonly panelOpen = signal(false);
  /** Werkkopie zolang het paneel open is; emit gebeurt pas bij sluiten. */
  protected readonly draft = signal<Record<string, number>>({});

  protected readonly badge = computed(() => Object.keys(this.selected()).length);

  protected readonly stateOptions: readonly { value: number; labelKey: 'defined' | 'undefined' | 'open' }[] = [
    { value: PlatformHdCenterState.Defined, labelKey: 'defined' },
    { value: PlatformHdCenterState.Undefined, labelKey: 'undefined' },
    { value: PlatformHdCenterState.Open, labelKey: 'open' },
  ];

  protected readonly text = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    return {
      label: translate('humanDesign.chartManager.centerFilter.label'),
      any: translate('humanDesign.chartManager.centerFilter.any'),
      apply: translate('humanDesign.chartManager.centerFilter.apply'),
      undefinedHint: translate('humanDesign.chartManager.centerFilter.undefinedHint'),
      clear: translate('humanDesign.chartManager.centerFilter.clear'),
      defined: translate('humanDesign.chartManager.centerFilter.defined'),
      undefined: translate('humanDesign.chartManager.centerFilter.undefined'),
      open: translate('humanDesign.chartManager.centerFilter.open'),
    };
  });

  /**
   * Knoppen per centerrij (gedeelde e-segmented-toggle): "–" = geen filter, daarna de
   * toestanden. De toggle werkt met strings; `ANY` en `String(state)` mappen terug in
   * {@link onStateChange}.
   */
  protected readonly toggleOptions = computed<readonly ESegmentedToggleOption[]>(() => {
    const text = this.text();
    return [
      { value: ANY_STATE, label: '–', title: text.any },
      ...this.stateOptions.map(option => ({
        value: String(option.value),
        label: text[option.labelKey],
        title: option.labelKey === 'undefined' ? text.undefinedHint : undefined,
      })),
    ];
  });

  /** De gekozen knop van een rij in de werkkopie. */
  protected stateValue(property: string): string {
    const state = this.draft()[property];
    return state === undefined ? ANY_STATE : String(state);
  }

  protected onStateChange(property: string, value: string): void {
    this.setState(property, value === ANY_STATE ? null : Number(value));
  }

  protected readonly rows = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    return HD_CENTER_FILTER_ROWS.map(row => ({
      property: row.property,
      name: this.transloco.translate<string>(`humanDesign.properties.centerNames.${row.nameKey}`, undefined, language),
    }));
  });

  protected togglePanel(): void {
    if (this.panelOpen()) {
      this.closeAndApply();
    } else {
      this.draft.set({ ...this.selected() });
      this.panelOpen.set(true);
    }
  }

  protected setState(property: string, state: number | null): void {
    this.draft.update(current => {
      const next = { ...current };
      if (state == null) {
        delete next[property];
      } else {
        next[property] = state;
      }
      return next;
    });
  }

  protected clearDraft(): void {
    this.draft.set({});
  }

  private closeAndApply(): void {
    this.panelOpen.set(false);
    const draft = this.draft();
    const selected = this.selected();
    const changed = Object.keys(draft).length !== Object.keys(selected).length
      || Object.entries(draft).some(([key, value]) => selected[key] !== value);
    if (changed) this.selectedChange.emit(draft);
  }

  /** Klik buiten de host sluit het paneel en past de wijzigingen toe (apply-on-close). */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.panelOpen()) return;
    if (event.target instanceof Node && this.host.nativeElement.contains(event.target)) return;
    this.closeAndApply();
  }
}
